import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Users, Plus, Trash2, Shield, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ username: "", password: "", cohort: true, financial: true });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const token = sessionStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/users`, { headers });
      setUsers(data.users || []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);

  const createUser = async () => {
    if (!newUser.username || !newUser.password) { setError("Username and password required"); return; }
    setCreating(true); setError("");
    const perms = [];
    if (newUser.cohort) perms.push("cohort");
    if (newUser.financial) perms.push("financial");
    try {
      const { data } = await axios.post(`${API}/users`, {
        username: newUser.username, password: newUser.password, permissions: perms
      }, { headers });
      if (data.error) { setError(data.error); }
      else { setNewUser({ username: "", password: "", cohort: true, financial: true }); loadUsers(); }
    } catch (e) { setError("Failed to create user"); }
    setCreating(false);
  };

  const updatePermissions = async (username, perms) => {
    await axios.put(`${API}/users/${username}`, { permissions: perms }, { headers });
    loadUsers();
  };

  const deleteUser = async (username) => {
    await axios.delete(`${API}/users/${username}`, { headers });
    loadUsers();
  };

  const togglePerm = (user, perm) => {
    const current = user.permissions || [];
    const updated = current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm];
    updatePermissions(user.username, updated);
  };

  return (
    <div className="space-y-5" data-testid="user-management-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">User Management</h1>
        <p className="text-sm text-slate-400 mt-1">Create and manage user access permissions</p>
      </div>

      {/* Create User */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="create-user-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Plus className="w-4 h-4 text-emerald-400" /> Create New User
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-xs text-slate-400">Username</Label>
              <Input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="username" className="bg-slate-800/60 border-slate-700 text-slate-200 h-9 text-sm mt-1"
                data-testid="new-username" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Password</Label>
              <Input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="password" type="password" className="bg-slate-800/60 border-slate-700 text-slate-200 h-9 text-sm mt-1"
                data-testid="new-password" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={newUser.cohort} onCheckedChange={(v) => setNewUser({ ...newUser, cohort: v })} data-testid="perm-cohort-new" />
                <span className="text-xs text-slate-300">Cohort</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={newUser.financial} onCheckedChange={(v) => setNewUser({ ...newUser, financial: v })} data-testid="perm-financial-new" />
                <span className="text-xs text-slate-300">Financial</span>
              </div>
            </div>
            <Button onClick={createUser} disabled={creating} className="bg-emerald-600 hover:bg-emerald-500 text-white h-9 text-sm" data-testid="create-user-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
            </Button>
          </div>
          {error && <p className="text-xs text-red-400 mt-2" data-testid="create-error">{error}</p>}
        </CardContent>
      </Card>

      {/* User List */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="users-list">
        <CardHeader className="pb-3">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" /> Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <div className="h-20 skeleton-shimmer rounded" /> : users.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No users created yet. Use the form above to create one.</p>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.username} className="flex items-center justify-between p-3 rounded-md bg-slate-800/30 border border-slate-800/50 hover:border-slate-700 transition-colors"
                  data-testid={`user-row-${u.username}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                      <span className="text-xs font-semibold text-slate-300">{u.username[0].toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">{u.username}</p>
                      <p className="text-[10px] text-slate-500">Created {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch checked={(u.permissions || []).includes("cohort")} onCheckedChange={() => togglePerm(u, "cohort")}
                        data-testid={`perm-cohort-${u.username}`} />
                      <Badge variant="outline" className={`text-[10px] ${(u.permissions || []).includes("cohort") ? "text-blue-400 border-blue-800" : "text-slate-600 border-slate-700"}`}>
                        Cohort
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={(u.permissions || []).includes("financial")} onCheckedChange={() => togglePerm(u, "financial")}
                        data-testid={`perm-financial-${u.username}`} />
                      <Badge variant="outline" className={`text-[10px] ${(u.permissions || []).includes("financial") ? "text-emerald-400 border-emerald-800" : "text-slate-600 border-slate-700"}`}>
                        Financial
                      </Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteUser(u.username)}
                      className="text-slate-500 hover:text-red-400 h-8 w-8 p-0" data-testid={`delete-${u.username}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
